"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Heart, Sparkles, Compass, Activity, Star, HeartHandshake } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
interface AudienceItem {
  id: string;
  iconName: string;
  title: string;
  description: string;
}
const audienceList: AudienceItem[] = [{
  id: "stress-exhaustion",
  iconName: "heart",
  title: "Stressgeplagte & Erschöpfte",
  description: "Für Menschen in stressigen Lebensphasen, die Erschöpfung und Überforderung spüren, bei der Arbeit, Weiterbildungen, Schule/ Ausbildung, Beziehung, Familie usw."
}, {
  id: "emotional-processing",
  iconName: "sparkles",
  title: "Emotionale Verarbeitung",
  description: "Für Menschen, die emotionale Themen oder alte Blockaden achtsam bearbeiten möchten, wie Trennung, Tod eines geliebten Menschen, Unfall, Verletzungen, Mobbing usw."
}, {
  id: "life-transitions",
  iconName: "compass",
  title: "Persönliche Umbruchphasen",
  description: "Für Menschen in persönlichen Umbruchphasen oder auf der Suche nach Neuorientierung, im Job, Selbständigkeit, räumliche/ örtliche Veränderungen usw."
}, {
  id: "physical-tension",
  iconName: "activity",
  title: "Körperliche Anspannung",
  description: "Für Menschen mit chronischen Verspannungen oder Schlafproblemen, die einen ganzheitlichen Ansatz suchen, bei Rücken/ Nackenschmerzen, Kopfschmerzen, Glieder/ Muskelschmerzen, Tinnitus, Schwindel, Herzrhythmusstörungen usw."
}, {
  id: "curious-beginners",
  iconName: "star",
  title: "Neugierige Einsteiger",
  description: "Für alle, die Energiearbeit interessiert, gespannt sind, etwas Neues ausprobieren möchten und offen, flexibel sind andere Ansätze kennenzulernen."
}, {
  id: "supportive-care",
  iconName: "hearthandshake",
  title: "Begleitende Unterstützung",
  description: "Für Menschen, die begleitend zu bestehender ärztlicher oder therapeutischer Behandlung einen ergänzenden Raum für Regeneration suchen."
}];
function renderAudienceIcon(iconName: string) {
  const iconProps = {
    className: "size-6 text-primary/80"
  };
  switch (iconName) {
    case "heart":
      return <Heart {...iconProps} />;
    case "sparkles":
      return <Sparkles {...iconProps} />;
    case "compass":
      return <Compass {...iconProps} />;
    case "activity":
      return <Activity {...iconProps} />;
    case "star":
      return <Star {...iconProps} />;
    case "hearthandshake":
      return <HeartHandshake {...iconProps} />;
    default:
      return <Sparkles {...iconProps} />;
  }
}
export default function TargetAudience() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="reikitargetaudience" data-nav="light" className="bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} variants={containerVariants} className="flex flex-col items-center text-center">
          {/* Header area */}
          <motion.div variants={itemVariants} className="flex flex-col items-center">
            <Badge variant="secondary" className="px-3 py-1 text-xs tracking-wide bg-secondary text-secondary-foreground">
              Ist Reiki das Richtige für Dich?
            </Badge>

            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-primary font-normal mt-4 tracking-tight">
              Für wen eignet sich Reiki?
            </h2>

            <p className="font-sans text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mt-6 mb-12 leading-relaxed">
              Reiki eignet sich für Erwachsene, die auf sanfte, achtsame Weise mehr
              innere Ruhe, Klarheit und Balance in ihr Leben bringen möchten –
              unabhängig von Vorerfahrung mit Energiearbeit.
            </p>
          </motion.div>

          {/* Audience Cards Grid */}
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 w-full text-left">
            {audienceList.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border/70 rounded-xl shadow-xs bg-card p-6 md:p-7 hover:shadow-md transition-shadow">
                  <CardHeader className="p-0 gap-0">
                    <div className="size-12 rounded-lg bg-secondary/60 flex items-center justify-center mb-5">
                      {renderAudienceIcon(item.iconName)}
                    </div>
                    <CardTitle className="font-serif text-xl md:text-2xl font-medium text-primary leading-snug">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 mt-3">
                    <p className="font-sans text-sm md:text-base text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>

          {/* Disclaimer Box */}
          <motion.div variants={itemVariants} className="w-full mt-12 md:mt-14">
            <div className="bg-muted/70 border border-border/80 rounded-xl p-6 md:p-8 max-w-2xl mx-auto text-center shadow-2xs">
              <p className="font-sans text-sm md:text-base text-muted-foreground leading-relaxed">
                Reiki ersetzt keine ärztliche oder psychotherapeutische Behandlung,
                sondern ergänzt sie achtsam.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}